import { put, call, take, fork } from 'redux-saga/effects'
import generalSaga from '../general'
import { SCORM_UPLOAD, SCORM_REPLACE } from '../../reducers/scorm/type'
import {
  scormCreateUploadUrlService,
  scormReplaceService,
  scormCreateMultipartUploadService,
  scormSignUploadPartService,
  scormCompleteUploadService,
  scormGetByIdService,
} from '../../api/scorm'
import {
  scormUploadSuccess,
  scormUploadError,
  scormReplaceSuccess,
  scormReplaceError,
} from '../../reducers/scorm/action'
import {
  operationSuccess,
  operationFail,
} from '../../reducers/operation/action'

const MULTIPART_THRESHOLD = 50 * 1024 * 1024 // 50 MiB
const PART_SIZE = 100 * 1024 * 1024 // 100 MiB
const MULTIPART_CONCURRENCY = 3
const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 60_000

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

function putWithXhr(url, body, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    if (contentType) xhr.setRequestHeader('Content-Type', contentType)

    if (onProgress) {
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100)
          onProgress(percent)
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const eTagRaw = xhr.getResponseHeader('etag') || xhr.getResponseHeader('ETag') || ''
        resolve({
          status: xhr.status,
          eTag: eTagRaw.replace(/"/g, ''),
        })
      } else {
        reject(new Error(`R2 upload failed: ${xhr.status}`))
      }
    }
    xhr.onerror = () => reject(new Error('R2 upload network error'))
    xhr.onabort = () => reject(new Error('R2 upload aborted'))

    xhr.send(body)
  })
}

async function putToR2(url, body, contentType, onProgress) {
  return putWithXhr(url, body, contentType, onProgress)
}

async function putPartWithRetry(url, blob, onProgress, attempts = 3) {
  let lastErr
  for (let i = 0; i < attempts; i++) {
    try {
      return await putWithXhr(url, blob, null, onProgress)
    } catch (e) {
      lastErr = e
      // Reset reported progress for this part on retry so totals don't overshoot
      if (onProgress) onProgress(0)
    }
  }
  throw lastErr
}

const unwrap = response => (response && response.data !== undefined ? response.data : response)

async function uploadSinglePut(file, blockId, onProgress) {
  const init = unwrap(await scormCreateUploadUrlService({
    fileName: file.name,
    contentLength: file.size,
    blockId,
  }))
  await putToR2(init.uploadUrl, file, 'application/zip', onProgress)
  if (onProgress) onProgress(100)
  await scormCompleteUploadService({ id: init.id })
  return init.id
}

async function uploadMultipart(file, blockId, onProgress) {
  const init = unwrap(await scormCreateMultipartUploadService({
    fileName: file.name,
    contentLength: file.size,
    blockId,
  }))

  const totalParts = Math.ceil(file.size / PART_SIZE)
  const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1)
  const parts = new Array(totalParts)
  const partProgress = new Array(totalParts).fill(0)

  const emitTotal = () => {
    if (!onProgress) return
    const sum = partProgress.reduce((a, b) => a + b, 0)
    onProgress(Math.round(sum / totalParts))
  }

  const uploadPart = async partNumber => {
    const start = (partNumber - 1) * PART_SIZE
    const blob = file.slice(start, Math.min(start + PART_SIZE, file.size))

    const signed = unwrap(await scormSignUploadPartService({
      key: init.key,
      uploadId: init.uploadId,
      partNumber,
    }))

    const res = await putPartWithRetry(signed.url, blob, percent => {
      partProgress[partNumber - 1] = percent
      emitTotal()
    })
    if (!res.eTag) {
      throw new Error(`Part ${partNumber}: ETag missing (check R2 CORS expose-headers)`)
    }
    partProgress[partNumber - 1] = 100
    emitTotal()
    parts[partNumber - 1] = { partNumber, eTag: res.eTag }
  }

  // Simple concurrency pool — drain partNumbers across N workers
  const queue = partNumbers.slice()
  const workers = Array.from({ length: Math.min(MULTIPART_CONCURRENCY, totalParts) }, async () => {
    while (queue.length) {
      const next = queue.shift()
      if (next === undefined) return
      await uploadPart(next)
    }
  })
  await Promise.all(workers)

  await scormCompleteUploadService({
    id: init.id,
    uploadId: init.uploadId,
    parts,
  })

  return init.id
}

const STATUS_READY = 3

async function pollUntilReady(id, blockId) {
  const start = Date.now()
  while (Date.now() - start < POLL_TIMEOUT_MS) {
    const pkg = unwrap(await scormGetByIdService(id, blockId))
    if (pkg) {
      const isReadyByStatus = pkg.status === STATUS_READY
      const isReadyByEntry = !!pkg.entryPointUrl
      if (isReadyByStatus || isReadyByEntry) return pkg
    }
    await delay(POLL_INTERVAL_MS)
  }
  throw new Error('SCORM processing timeout')
}

async function runUpload(file, blockId, onProgress) {
  const id = file.size > MULTIPART_THRESHOLD
    ? await uploadMultipart(file, blockId, onProgress)
    : await uploadSinglePut(file, blockId, onProgress)
  return await pollUntilReady(id, blockId)
}

function* scormUploadFlow(payload) {
  const { file, blockId, callback, onProgress } = payload
  try {
    const pkg = yield call(runUpload, file, blockId, onProgress)

    yield put(scormUploadSuccess(pkg))
    yield put(operationSuccess())

    if (callback) callback(pkg, null)
  } catch (error) {
    yield put(scormUploadError())
    yield put(operationFail(error))
    if (callback) callback(null, error)
  }
}

export default function* scormUploadWatcher() {
  while (true) {
    const action = yield take(SCORM_UPLOAD)
    const { payload } = action

    yield fork(generalSaga, scormUploadFlow, payload)
  }
}

// ── Replace flow — reuses PUT + CompleteUpload + polling ──

async function runReplace(file, scormId, blockId, onProgress) {
  const init = unwrap(await scormReplaceService(scormId, {
    fileName: file.name,
    contentLength: file.size,
  }))
  // Backend may return a fresh id but Replace keeps the same scormId — be defensive
  const targetId = (init && init.id) || scormId
  await putToR2(init.uploadUrl, file, 'application/zip', onProgress)
  if (onProgress) onProgress(100)
  await scormCompleteUploadService({ id: targetId })
  return await pollUntilReady(targetId, blockId)
}

function* scormReplaceFlow(payload) {
  const { file, scormId, blockId, callback, onProgress } = payload
  try {
    const pkg = yield call(runReplace, file, scormId, blockId, onProgress)

    yield put(scormReplaceSuccess(pkg))
    yield put(operationSuccess())

    if (callback) callback(pkg, null)
  } catch (error) {
    yield put(scormReplaceError())
    yield put(operationFail(error))
    if (callback) callback(null, error)
  }
}

export function* scormReplaceWatcher() {
  while (true) {
    const action = yield take(SCORM_REPLACE)
    const { payload } = action

    yield fork(generalSaga, scormReplaceFlow, payload)
  }
}
